import React, { useState } from "react";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { Calendar, Printer, Download, Filter } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { ReportActionToolbar } from "../../components/reports/ReportActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import {
  AppointmentsTable,
  AppointmentReportItem,
} from "../../components/reports/AppointmentsTable";
import { ReportDateRangePicker } from "../../components/reports/ReportDateRangePicker";
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  format,
} from "date-fns";
import { useAuth } from "../../auth/AuthContext";

const generateMockAppointmentsData = (currencySymbol: string): AppointmentReportItem[] => {
  const staff = ["Alice M.", "Bob D.", "Charlie R.", "Diana P."];
  const customers = ["Jane Doe", "John Smith", "Emma Wilson", "Liam Brown", "Walk-in Guest", "Olivia Jones", "Noah Miller"];
  const services = ["Haircut", "Coloring", "Manicure", "Pedicure", "Massage", "Facial"];
  const statuses: ("Scheduled" | "Completed" | "Cancelled" | "No Show")[] = ["Scheduled", "Completed", "Completed", "Cancelled", "No Show"];

  return Array.from({ length: 45 }).map((_, i) => {
    const isToday = i < 6;
    const date = subDays(new Date(), isToday ? 0 : Math.floor(i / 6));
    const hour = 9 + (i % 9);
    date.setHours(hour, 0);

    const priceBase = Math.floor(Math.random() * 80) + 20;

    return {
      id: `apt-${i}`,
      date: format(date, "EEE, dd MMM yyyy"),
      time: format(date, "hh:mm a"),
      dateObj: date,
      customerName: customers[Math.floor(Math.random() * customers.length)],
      staffName: staff[Math.floor(Math.random() * staff.length)],
      serviceName: services[Math.floor(Math.random() * services.length)],
      duration: ["30m", "45m", "60m"][Math.floor(Math.random() * 3)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      price: `${currencySymbol}${priceBase.toFixed(2)}`,
    };
  });
};

export default function AppointmentsReportPage() {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const [appointments] = useState<any[]>(() => generateMockAppointmentsData(currencySymbol));
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [isLoading, setIsLoading] = useState(false);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: startOfMonth(new Date()),
    end: new Date(),
  });

  const filteredAppointments = appointments.filter((item) => {
    const matchesSearch =
      item.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.staffName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serviceName.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDate = true;
    if (dateRange.start && dateRange.end && item.dateObj) {
      matchesDate =
        item.dateObj >= startOfDay(dateRange.start) &&
        item.dateObj <= endOfDay(dateRange.end);
    }

    return matchesSearch && matchesDate;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAppointments = filteredAppointments.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="flex-1 flex flex-col space-y-6 overflow-hidden p-4">
      <SubPageHeader
        title="Appointments Report"
        subtitle="Overview of scheduled, completed, and cancelled appointments"
        icon={Calendar}
        actions={
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 lg:gap-3 w-full sm:w-auto">
            <div className="flex-1 sm:flex-none min-w-[140px]">
              <ReportDateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={(start, end) => setDateRange({ start, end })}
              />
            </div>
            <Button
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center justify-center gap-2 h-10 px-4 sm:px-3 shadow-sm rounded-xl"
              title="Print Report"
            >
              <Printer size={18} />
              <span className="sm:hidden font-bold">Print</span>
            </Button>
          </div>
        }
      />

      <ReportActionToolbar
        totalItems={appointments.length}
        showingStart={filteredAppointments.length > 0 ? indexOfFirstItem + 1 : 0}
        showingEnd={Math.min(indexOfLastItem, filteredAppointments.length)}
        searchTerm={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search customer, staff, or service..."
      >
        <Button
          variant="outline"
          className="h-[38px] w-[38px] p-0 flex items-center justify-center border-gray-200 text-gray-500 hover:text-[#885ABB] hover:border-[#885ABB] bg-white"
          title="Export CSV"
        >
          <Download size={18} />
        </Button>
      </ReportActionToolbar>

      <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
        <AppointmentsTable
          appointments={currentAppointments}
          isLoading={isLoading}
          indexOfFirstItem={indexOfFirstItem}
        />
        <PaginationFooter
          totalItems={appointments.length}
          filteredTotal={filteredAppointments.length}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}
